"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.composeTrackingConsentText = exports.recordedSentenceKey = exports.stopPageOffersRefuse = exports.fillTrackingConsentText = exports.trackingConsentKindOf = exports.senderTracksOpensOrClicks = exports.refusalDeletesNothingNote = exports.refusalStopsTrackingNote = exports.trackingConsentReplacedSentences = exports.trackingConsentCustomizableTexts = exports.trackingConsentTexts = void 0;
const constants_1 = require("./constants");
const privacy_policy_url_1 = require("./privacy_policy_url");
/**
 * The sentences of the email tracking pages a contact sees (the stop page
 * behind the footer link, and the row on the preferences page), and the one
 * function that puts them together.
 *
 * The page and the server both use composeTrackingConsentText: the page to
 * draw its text, the server to write down "the text this person saw" next to
 * their choice. They can never differ, because there is only one function.
 * That is also why the sentences live here and not in the frontend.
 *
 * Every page says at most two short sentences (Vince, 16 October: anything
 * longer is in the help article, not on the screen). Each sentence below is
 * one sentence, and a page is put together from two of them at most.
 *
 * A sentence is also the key of the account's own text for it: the Customize
 * page lists these, and the account's snippets are looked up by the sentence.
 * {{email}} and {{accountName}} are replaced in every sentence.
 */
exports.trackingConsentTexts = {
    // the stop page, a contact who is tracked. The sentence for what is
    // recorded comes first (recordedOpensAndClicks and its two variants)
    refuseTitle: "Refuse email tracking?",
    // what refusing does: said before the button, to every contact who can still
    // refuse (tracked or not asked yet: a contact not asked yet can own opens and
    // clicks from before the account turned the choice on or from before its
    // cut-off date). Tracking stops at once, and what was recorded before stays.
    // It speaks of what Encharge records only: copies in other tools are not
    // Encharge's to change, and the page does not promise anything about them
    refuseEffect: "If you refuse, we stop tracking at once, and what we recorded before stays.",
    // also the words of the footer link in an email, see stopTrackingLinkDefaultText
    refuseButton: constants_1.stopTrackingLinkDefaultText,
    // the stop page, after refusing or already refused
    offTitle: "Email tracking is off",
    // the result, for a contact who refused on this page just now (the title
    // says tracking is off)
    offStopped: "We stopped tracking at once, and what we recorded before stays.",
    // the stop page, a contact who refused before opening it, instead of
    // offStopped: it still offers them the Refuse button. A contact refused
    // another way can refuse here too, in their own name; this page cannot tell
    // how they refused, so it says this to everyone who refused before opening
    // it. Not said once they refused on the page itself (offStopped then)
    refuseAgainEffect: "If you refuse here, tracking stays off, and what we recorded before stays.",
    // the lead-in of what is recorded, said before the choice to allow tracking
    // (the sentence for what is recorded follows it, as one sentence)
    allowIntro: "If you allow email tracking:",
    allowButton: "Allow email tracking",
    // what is recorded, on the stop page and on the preferences row: opens and
    // clicks, or only one of them when the account tracks only one
    recordedOpensAndClicks: "We record when you open our emails and click their links, which can include your approximate location.",
    recordedOpens: "We record when you open our emails, which can include your approximate location.",
    recordedClicks: "We record when you click links in our emails, which can include your approximate location.",
    // an account that tracks neither opens nor clicks
    senderDoesNotTrack: "This sender doesn't track opens or clicks of its emails.",
    // the row on the preferences page: what is recorded, then this
    toggleLabel: "Email tracking",
    // what turning the switch off does: said on the row of a contact who is
    // tracked, before the switch (it saves at once). A contact not asked yet has
    // a Refuse button instead and reads refuseEffect
    rowRefuseEffect: "If you turn this off, we stop tracking at once, and what we recorded before stays.",
    // the link to the account's privacy policy, when the account has set one:
    // on the stop page and on the row, after the text
    moreInfo: "Read more about how we use email tracking",
    // the messages after saving
    saved: "Tracking preference updated.",
    saveError: "Error while updating your tracking preference.",
};
/** The sentences the Customize page lets an account translate or reword. */
exports.trackingConsentCustomizableTexts = Object.values(exports.trackingConsentTexts);
/**
 * The default sentences that these ones replaced when the pages were cut to two
 * sentences (16 October), by the new sentence. The account's own wording is
 * saved under the default sentence it replaced, so an account that reworded the
 * old default keeps its wording: the pages read it when the account has not
 * reworded the new one. Sentences that left the pages (the purpose, "It applies
 * to", the note about saving, the line about allowing again after a day, the
 * off and not asked sentences the title now says, every sentence about what
 * refusing did to what was recorded, and the note under the switch of a contact
 * who refused, gone on 28 October) have no entry: nothing reads them any
 * more, and an old wording of them must never stand under a new sentence. The
 * two "row" sentences about what is recorded were merged into the stop page's,
 * which now read the same.
 */
exports.trackingConsentReplacedSentences = {
    [exports.trackingConsentTexts.recordedOpensAndClicks]: [
        "We record when you open our emails and which links you click, on every device you read them on. This can include your approximate location.",
        "We record when you open our emails and which links you click, on every device you use. This can include your approximate location.",
    ],
    [exports.trackingConsentTexts.recordedOpens]: [
        "We record when you open our emails, on every device you read them on. This can include your approximate location.",
        "We record when you open our emails, on every device you use. This can include your approximate location.",
    ],
    [exports.trackingConsentTexts.recordedClicks]: [
        "We record which links you click in our emails, on every device you read them on. This can include your approximate location.",
        "We record which links you click in our emails, on every device you use. This can include your approximate location.",
    ],
};
/**
 * What a team member, a flow, an import or the API is told wherever a contact
 * can be set to refused: tracking stops at once, and the opens and clicks
 * already recorded stay. Two short sentences for every place, so they cannot
 * drift. It is not one of the pages' sentences (a contact never reads it, and
 * an account does not reword it), so it is not in trackingConsentTexts.
 */
exports.refusalStopsTrackingNote = "Tracking stops at once. The opens and clicks already recorded for this contact stay.";
/**
 * The first sentence of refusalStopsTrackingNote, for the places that have room
 * for one sentence next to another (the Change field step in a flow). A spec
 * pins that the long note starts with it.
 */
exports.refusalDeletesNothingNote = "Tracking stops at once.";
/** Does the sender track anything at all? Not when it turned off both. */
const senderTracksOpensOrClicks = ({ tracksOpens, tracksClicks, }) => tracksOpens || tracksClicks;
exports.senderTracksOpensOrClicks = senderTracksOpensOrClicks;
/**
 * The version of the page for a stored choice.
 * - `storedConsent`: "granted", "denied" or anything else for never asked
 * - `trackingActive`: is the contact tracked right now, after the account
 *   switches? When missing, everyone but a contact who refused is.
 * - `senderTracks`: false when the account tracks neither opens nor clicks
 */
const trackingConsentKindOf = ({ storedConsent, trackingActive, senderTracks, }) => {
    if (storedConsent === "denied")
        return "refused";
    if (!senderTracks)
        return "tracked";
    const tracked = trackingActive !== null && trackingActive !== void 0 ? trackingActive : true;
    return tracked ? "tracked" : "notAsked";
};
exports.trackingConsentKindOf = trackingConsentKindOf;
/**
 * Put the contact's address and the account's name into a sentence. One pass,
 * so a value that happens to contain a placeholder is never replaced again.
 */
const fillTrackingConsentText = (text, values) => text.replace(/\{\{(email|accountName)\}\}/g, (_match, name) => { var _a, _b; return name === "email" ? (_a = values.email) !== null && _a !== void 0 ? _a : "" : (_b = values.accountName) !== null && _b !== void 0 ? _b : ""; });
exports.fillTrackingConsentText = fillTrackingConsentText;
/**
 * Does the stop page offer "Refuse email tracking"? To a contact who is
 * tracked or not asked yet, and to a contact who refused, unless they refused
 * on the page itself just now. A contact refused another way (a signup form,
 * its confirmation, an import, the API, a team member, a flow) can refuse
 * here, which records the refusal in their own name; the page cannot tell how
 * they refused, so it offers it to everyone who refused before opening it. The
 * page and its text both read this, so the button is there exactly when the
 * text says it is.
 */
const stopPageOffersRefuse = ({ kind, refusedOnThisPage, }) => kind !== "refused" || !refusedOnThisPage;
exports.stopPageOffersRefuse = stopPageOffersRefuse;
/**
 * The sentence for what is recorded, for what the account tracks. Only
 * `tracksOpens` and `tracksClicks` decide it, so a page never says "we record"
 * about something the sender does not track. The stop page and the row say the
 * same thing.
 */
const recordedSentenceKey = ({ tracksOpens, tracksClicks, }) => {
    if (tracksOpens && !tracksClicks)
        return exports.trackingConsentTexts.recordedOpens;
    if (tracksClicks && !tracksOpens)
        return exports.trackingConsentTexts.recordedClicks;
    return exports.trackingConsentTexts.recordedOpensAndClicks;
};
exports.recordedSentenceKey = recordedSentenceKey;
/**
 * The text of a page, in the account's own wording and with the address
 * filled. `text` is the proof: what the person read before they chose. Every
 * page is two sentences at most.
 *
 * Every version that offers "Refuse email tracking", or a switch that refuses
 * when turned off, says what refusing does BEFORE the choice (refuseEffect, or
 * rowRefuseEffect on the switch): tracking stops at once, and what was
 * recorded before stays. That holds whatever the state: a contact not asked
 * yet can own opens and clicks from before the account turned the choice on or
 * from before its cut-off date, and a sender that now tracks nothing may have
 * tracked before.
 *
 * The title says whether tracking is on or off, so the sentences say what each
 * button does.
 * - stop page, tracked: what is recorded, then what refusing does.
 * - stop page, refused just now on the page: that tracking stopped and what was
 *   recorded before stays, then what allowing would record.
 * - stop page, refused before opening it: what refusing here does
 *   (refuseAgainEffect, because the Refuse button is still there), then what
 *   allowing would record.
 * - stop page, not asked yet: what refusing does, then what allowing would
 *   record.
 * - preferences row: what is recorded, then what turning the switch off does
 *   (a contact who is tracked), or what refusing does (a contact not asked
 *   yet, who has a Refuse button), or nothing more (a contact who refused:
 *   the switch is off).
 * - an account that tracks nothing: one sentence, then what refusing does for a
 *   contact who has not refused (they still have the Refuse button). On the
 *   preferences row a contact who refused has nothing left to choose, so the
 *   sentence alone; the stop page still offers them to refuse there, as above.
 */
const composeTrackingConsentText = (input) => {
    const t = exports.trackingConsentTexts;
    // The account's wording of a sentence: under the sentence itself, or else
    // under a default it replaced (trackingConsentReplacedSentences)
    const own = (sentence) => {
        var _a;
        return [sentence, ...((_a = exports.trackingConsentReplacedSentences[sentence]) !== null && _a !== void 0 ? _a : [])]
            .map((key) => { var _a; return (_a = input.snippets) === null || _a === void 0 ? void 0 : _a[key]; })
            .find(Boolean);
    };
    const translate = (sentence) => (0, exports.fillTrackingConsentText)(own(sentence) || sentence, {
        email: input.email,
        accountName: input.accountName,
    });
    const senderTracks = (0, exports.senderTracksOpensOrClicks)(input);
    const recorded = translate((0, exports.recordedSentenceKey)(input));
    const common = {
        refuseLabel: translate(t.refuseButton),
        allowLabel: translate(t.allowButton),
        offLabel: translate(t.offTitle),
    };
    const moreInfoUrl = (0, privacy_policy_url_1.normalizePrivacyPolicyUrl)(input.privacyPolicyUrl);
    const moreInfo = moreInfoUrl
        ? { label: translate(t.moreInfo), url: moreInfoUrl }
        : undefined;
    const done = (title, body, { allow } = {}) => (Object.assign(Object.assign({}, common), { title,
        body,
        allow,
        moreInfo, text: [body, allow, moreInfo && `${moreInfo.label} (${moreInfo.url})`]
            .filter(Boolean)
            .join(" ") }));
    const refused = input.kind === "refused";
    // A contact who has not refused can refuse here: they read what refusing
    // does first
    const refuseEffect = translate(t.refuseEffect);
    if (input.surface === "preferences row") {
        const title = translate(t.toggleLabel);
        if (!senderTracks) {
            return done(title, refused
                ? translate(t.senderDoesNotTrack)
                : `${translate(t.senderDoesNotTrack)} ${refuseEffect}`);
        }
        // the switch of a contact who is tracked refuses when it is turned off;
        // a contact not asked yet has a Refuse button; a contact who refused
        // reads only what is recorded, next to the switch that is off
        const effect = refused
            ? undefined
            : input.kind === "tracked"
                ? translate(t.rowRefuseEffect)
                : refuseEffect;
        return done(title, [recorded, effect].filter(Boolean).join(" "));
    }
    const title = translate(input.kind === "tracked" ? t.refuseTitle : t.offTitle);
    // a contact who refused is still offered to refuse here (refused another
    // way), except right after refusing on this page
    const refuseAgain = refused && (0, exports.stopPageOffersRefuse)(input)
        ? translate(t.refuseAgainEffect)
        : undefined;
    if (!senderTracks) {
        return done(title, refused
            ? [translate(t.senderDoesNotTrack), refuseAgain]
                .filter(Boolean)
                .join(" ")
            : `${translate(t.senderDoesNotTrack)} ${refuseEffect}`);
    }
    if (input.kind === "tracked") {
        return done(title, `${recorded} ${refuseEffect}`);
    }
    // what allowing would record, said before the Allow button
    const allow = `${translate(t.allowIntro)} ${recorded}`;
    if (refused) {
        // one sentence about refusing: the one that also says they can refuse here
        // when they refused before opening the page, the result when they refused
        // on it just now; then what allowing would record
        return done(title, refuseAgain !== null && refuseAgain !== void 0 ? refuseAgain : translate(t.offStopped), { allow });
    }
    return done(title, refuseEffect, { allow });
};
exports.composeTrackingConsentText = composeTrackingConsentText;
//# sourceMappingURL=trackingConsentTexts.js.map